import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiBookmark,
  FiFilter,
  FiSearch,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiVolume2,
  FiRotateCw,
  FiClock,
  FiTrendingUp,
  FiCheckCircle,
  FiXCircle,
  FiZap,
  FiStar,
  FiCalendar,
  FiBook,
  FiInfo,
  FiDownload,
  FiUpload,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import {
  redirectToLoginWithReturn,
  speakWord,
} from '../../../utils/utils';
import { renderNuanceInlineMarkdown } from '../../../utils/renderNuanceInlineMarkdown';
import {
  fetchFlashcards as apiFetchFlashcards,
  addFlashcard as apiAddFlashcard,
  updateFlashcard as apiUpdateFlashcard,
  deleteFlashcard as apiDeleteFlashcard,
  bulkAddFlashcards as apiBulkAddFlashcards,
  fetchReviewHistory as apiFetchReviewHistory,
  recordReviewEvent as apiRecordReviewEvent,
  syncPendingChanges,
  bulkImportReviewHistory as apiBulkImportReviewHistory,
  fetchOldConfigFlashcards,
  cleanOldConfigFlashcards,
  getFlashcardCount as apiGetFlashcardCount,
  lookupGreWords as apiLookupGreWords,
} from './api/flashcardAPI';
import Sidebar from '../../Sidebar';
import GREWordsChallenge from '../../../components/GREWordsChallenge';
import { greWordsData, greNuanceMap as _nuanceMap } from '../../../utils/greVocabularyData';
import { markHomeTaskEvent } from '../../home/homeTaskStatus';
import './FlashcardBook.css';
import '../../Core.css';

const createManualCardDraft = () => ({
  word: '',
  definition_en: '',
  definition_fr: '',
  example_sentence_en: '',
  example_sentence_fr: '',
  difficulty_level: 'medium',
  emoticon: '',
  personalNote: '',
});

const normalizeFlashcardWord = (word = '') =>
  String(word)
    .trim()
    .toLowerCase();

const mapGreDifficultyToCardDifficulty = (difficulty = '') => {
  switch (String(difficulty).trim().toLowerCase()) {
    case 'easy':
      return 'easy';
    case 'hard':
      return 'hard';
    default:
      return 'medium';
  }
};

const CSV_IMPORT_BATCH_SIZE = 500;

const CSV_COLUMN_ALIASES = {
  word: ['word', 'word en', 'mot', 'mot en', 'term', 'terme'],
  definition_en: [
    'definition',
    'definition en',
    'definition english',
    'definition anglais',
    'meaning',
    'meaning en',
  ],
  definition_fr: [
    'definition fr',
    'definition french',
    'definition francais',
    'meaning fr',
    'traduction',
    'traduction fr',
  ],
  example_sentence_en: [
    'example',
    'example en',
    'example sentence',
    'example sentence en',
    'example english',
    'exemple',
    'exemple en',
    'phrase en',
  ],
  example_sentence_fr: [
    'example fr',
    'example sentence fr',
    'example french',
    'exemple fr',
    'phrase fr',
    'traduction exemple',
  ],
  difficulty_level: ['difficulty', 'difficulty level', 'level', 'niveau'],
  emoticon: ['emoji', 'emoticon', 'icon'],
  personalNote: ['note', 'notes', 'personal note', 'personal notes', 'memo'],
  question: ['question', 'quiz question', 'prompt'],
  options: ['options', 'choices', 'answers', 'reponses', 'reponse options'],
};

const normalizeCsvHeader = (header = '') =>
  String(header)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const resolveCsvColumnKey = (header = '') => {
  const normalizedHeader = normalizeCsvHeader(header);
  return (
    Object.entries(CSV_COLUMN_ALIASES).find(([, aliases]) =>
      aliases.includes(normalizedHeader)
    )?.[0] || null
  );
};

const detectCsvDelimiter = (text = '') => {
  const firstLine = String(text)
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .find((line) => line.trim());

  if (!firstLine) return ',';

  const candidates = [',', ';', '\t'];
  let bestDelimiter = ',';
  let bestScore = -1;

  candidates.forEach((delimiter) => {
    const score = firstLine.split(delimiter).length;
    if (score > bestScore) {
      bestScore = score;
      bestDelimiter = delimiter;
    }
  });

  return bestDelimiter;
};

const parseDelimitedRows = (text = '', delimiter = ',') => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const input = String(text).replace(/^\uFEFF/, '');

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];
    const nextChar = input[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      row.push(field);
      field = '';
      continue;
    }

    if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i += 1;
      }
      row.push(field);
      if (row.some((cell) => String(cell).trim() !== '')) {
        rows.push(row);
      }
      row = [];
      field = '';
      continue;
    }

    field += char;
  }

  row.push(field);
  if (row.some((cell) => String(cell).trim() !== '')) {
    rows.push(row);
  }

  if (inQuotes) {
    throw new Error('The CSV file contains an unclosed quote.');
  }

  return rows;
};

const getCsvCell = (row, columnMap, field) => {
  const index = columnMap[field];
  if (index == null) return '';
  return String(row[index] || '').trim();
};

const parseCsvOptions = (rawValue = '') => {
  const value = String(rawValue || '').trim();
  if (!value) return null;

  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  if (value.includes('|')) {
    const options = value
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean);
    return options.length ? options : null;
  }

  return null;
};

// Spaced Repetition Algorithm (SM-2 Algorithm - similar to Anki)
const calculateNextReview = (easeFactor, interval, quality) => {
  // quality: 0-5 (0=complete blackout, 5=perfect response)
  let newEaseFactor = easeFactor;
  let newInterval = interval;

  if (quality < 3) {
    // Failed - reset interval to 1 day
    newInterval = 1;
  } else {
    // Passed - calculate new interval
    if (interval === 0) {
      newInterval = 1;
    } else if (interval === 1) {
      newInterval = 6;
    } else {
      newInterval = Math.round(interval * easeFactor);
    }

    // Update ease factor
    newEaseFactor =
      easeFactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    newEaseFactor = Math.max(1.3, newEaseFactor); // Minimum ease factor
  }

  const nextReviewDate = new Date();
  nextReviewDate.setDate(nextReviewDate.getDate() + newInterval);

  return {
    easeFactor: newEaseFactor,
    interval: newInterval,
    nextReviewDate: nextReviewDate.toISOString(),
  };
};

const FlashcardBook = () => {
  const navigate = useNavigate();
  const authRedirectStartedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const [flashcards, setFlashcards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all'); // all, due, new, learning, mastered
  const [filterAdded, setFilterAdded] = useState('all'); // all, last7d
  const [sortBy, setSortBy] = useState('dateAdded'); // dateAdded, nextReview, difficulty, alphabetical
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [selectedCard, setSelectedCard] = useState(null);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [reviewCards, setReviewCards] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [manualCardDraft, setManualCardDraft] = useState(createManualCardDraft);
  const [manualAddError, setManualAddError] = useState('');
  const [isAddingManualCard, setIsAddingManualCard] = useState(false);
  const [actionFeedback, setActionFeedback] = useState({
    type: '',
    message: '',
  });
  const [showCsvFormatInfo, setShowCsvFormatInfo] = useState(false);
  const [csvImportPreview, setCsvImportPreview] = useState(null);
  const [isImportingCsv, setIsImportingCsv] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(null);
  const [showInfo, setShowInfo] = useState(null);
  const [showWordsChallenge, setShowWordsChallenge] = useState(false);
  const [showReviewModeSelector, setShowReviewModeSelector] = useState(false);
  const [showBrowseLibrary, setShowBrowseLibrary] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');
  const [browseDifficulty, setBrowseDifficulty] = useState('all');
  const [browseLetter, setBrowseLetter] = useState('all');
  const [browseSelected, setBrowseSelected] = useState(new Set());
  const [isAddingBrowse, setIsAddingBrowse] = useState(false);
  const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [greWordByKey, setGreWordByKey] = useState({});
  const csvImportInputRef = useRef(null);
  const actionFeedbackTimeoutRef = useRef(null);
  const autoReviewOpenedRef = useRef(false);

  const userEmail = React.useMemo(() => {
    let raw = null;
    try {
      const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
      raw =
        sessionUser?.email ||
        sessionUser?.user_email ||
        sessionUser?.userEmail ||
        null;
    } catch {
      raw = null;
    }

    if (!raw) raw = localStorage.getItem('userEmail');
    const normalized = String(raw || '')
      .trim()
      .toLowerCase();

    if (!normalized || normalized === 'undefined' || normalized === 'null') {
      return 'default';
    }
    return normalized;
  }, []);

  const [showGuide, setShowGuide] = useState(() => {
    try {
      return localStorage.getItem(`flashcard_guide_dismissed_${userEmail}`) !== 'true';
    } catch { return true; }
  });

  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);

  const handleAuthFailure = (
    error,
    message = 'Votre session a expire. Veuillez vous reconnecter.'
  ) => {
    if (error?.message !== 'Authentication failed') {
      return false;
    }

    if (!authRedirectStartedRef.current) {
      authRedirectStartedRef.current = true;
      redirectToLoginWithReturn({
        navigate,
        redirectTo: window.location.pathname + window.location.search,
        message,
      });
    }

    return true;
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (actionFeedbackTimeoutRef.current) {
        clearTimeout(actionFeedbackTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (userEmail === 'default') {
      handleAuthFailure(new Error('Authentication failed'));
      return;
    }
    loadFlashcards();

    const handleFocus = () => {
      loadFlashcards();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [userEmail, navigate]);

  // Apply filters and search
  useEffect(() => {
    applyFiltersAndSearch();
  }, [flashcards, searchQuery, filterDifficulty, filterStatus, filterAdded, sortBy]);

  // Check for review parameter to open flashcard review automatically.
  // `review=true` means flip-card review, not the GRE Words Challenge game.
  useEffect(() => {
    if (searchParams.get('review') !== 'true') {
      autoReviewOpenedRef.current = false;
      return;
    }

    if (
      autoReviewOpenedRef.current ||
      flashcards.length === 0 ||
      isReviewMode
    ) {
      return;
    }

    const now = new Date();
    let cards = flashcards.filter(
      (card) => new Date(card.nextReviewDate) <= now
    );
    if (cards.length === 0) {
      const last7Days = new Date(now);
      last7Days.setDate(last7Days.getDate() - 7);
      cards = flashcards.filter((card) => {
        if (!card.addedAt) return false;
        return new Date(card.addedAt) >= last7Days;
      });
    }
    if (cards.length === 0) cards = [...flashcards];

    autoReviewOpenedRef.current = true;
    setShowWordsChallenge(false);
    setShowReviewModeSelector(false);
    setReviewCards(cards.sort(() => Math.random() - 0.5));
    setCurrentReviewIndex(0);
    setShowAnswer(false);
    setIsReviewMode(true);
  }, [searchParams, flashcards, isReviewMode]);

  const loadFlashcards = async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    try {
      if (userEmail && userEmail !== 'default') {
        await syncPendingChanges(userEmail);
      }

      const [cards, history] = await Promise.all([
        apiFetchFlashcards(userEmail),
        apiFetchReviewHistory(userEmail),
      ]);

      let finalCards = cards;
      if (finalCards.length === 0 && userEmail !== 'default') {
        const migrated = await migrateLocalStorageFlashcards(userEmail);
        if (migrated) {
          finalCards = await apiFetchFlashcards(userEmail);
        }
      }

      if (isMountedRef.current) {
        setFlashcards(finalCards);
        setReviewHistory(history);
      }
      resolveGreWordsForCards(finalCards);
    } catch (error) {
      if (handleAuthFailure(error)) {
        return;
      }
      console.error('Error loading flashcards:', error);
    } finally {
      isLoadingRef.current = false;
    }
  };

  const migrateLocalStorageFlashcards = async (email) => {
    try {
      // 1. Collect cards from localStorage (primary key + any mismatched key)
      let lsCards = [];
      let lsHistory = [];
      const primaryKey = `gre_flashcards_${email}`;
      let raw = localStorage.getItem(primaryKey);

      if (!raw) {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith('gre_flashcards_') && !k.includes('backup') && !k.includes('pending') && !k.includes('mastered') && !k.includes('review_history')) {
            raw = localStorage.getItem(k);
            if (raw) break;
          }
        }
      }

      if (raw) {
        try { lsCards = JSON.parse(raw); } catch { lsCards = []; }
        if (!Array.isArray(lsCards)) lsCards = [];
      }

      const historyRaw = localStorage.getItem(`gre_flashcard_review_history_${email}`);
      if (historyRaw) {
        try { lsHistory = JSON.parse(historyRaw); } catch { lsHistory = []; }
        if (!Array.isArray(lsHistory)) lsHistory = [];
      }

      // 2. Collect cards from old UserConfiguration sync backend
      const oldConfig = await fetchOldConfigFlashcards(email);
      const configCards = oldConfig.cards || [];
      const configHistory = oldConfig.history || [];

      // 3. Merge by word, keeping whichever copy has more reviewCount
      const mergedMap = new Map();
      for (const card of configCards) {
        if (card && card.word) mergedMap.set(card.word, card);
      }
      for (const card of lsCards) {
        if (!card || !card.word) continue;
        const existing = mergedMap.get(card.word);
        if (!existing) {
          mergedMap.set(card.word, card);
        } else if ((card.reviewCount || 0) > (existing.reviewCount || 0)) {
          mergedMap.set(card.word, card);
        } else if (
          (card.reviewCount || 0) === (existing.reviewCount || 0) &&
          card.lastReviewed && existing.lastReviewed &&
          new Date(card.lastReviewed) > new Date(existing.lastReviewed)
        ) {
          mergedMap.set(card.word, card);
        }
      }
      const mergedCards = Array.from(mergedMap.values());

      // Merge review history by date
      const historyMap = new Map();
      for (const e of configHistory) { if (e && e.date) historyMap.set(e.date, e); }
      for (const e of lsHistory) {
        if (!e || !e.date) continue;
        const existing = historyMap.get(e.date);
        if (!existing || (e.cardsReviewed || e.cards_reviewed || 0) > (existing.cardsReviewed || existing.cards_reviewed || 0)) {
          historyMap.set(e.date, e);
        }
      }
      const mergedHistory = Array.from(historyMap.values());

      if (mergedCards.length === 0) return false;

      // 4. Backup before any destructive action
      try {
        localStorage.setItem(
          `gre_flashcards_backup_${email}`,
          JSON.stringify({ cards: mergedCards, history: mergedHistory, backedUpAt: new Date().toISOString() })
        );
      } catch { /* quota — non-critical */ }

      // 5. Push to new DB
      console.log(`Migrating ${mergedCards.length} flashcards to DB...`);
      await apiBulkAddFlashcards(email, mergedCards);

      if (mergedHistory.length > 0) {
        try { await apiBulkImportReviewHistory(email, mergedHistory); } catch { /* non-critical */ }
      }

      // 6. Verify the new DB actually has the data (use unique words as baseline since backend deduplicates)
      const uniqueWords = new Set(mergedCards.map((c) => (c.word || '').trim().toLowerCase()).filter(Boolean));
      const dbCount = await apiGetFlashcardCount(email);
      if (dbCount < uniqueWords.size) {
        console.warn(
          `Migration verification failed: sent ${uniqueWords.size} unique words but DB has ${dbCount}. Keeping localStorage as safety net.`
        );
        return false;
      }

      // 7. Cleanup: remove from localStorage and old config blob
      console.log(`Migration verified (${dbCount} cards in DB). Cleaning up old sources.`);
      try {
        localStorage.removeItem(`gre_flashcards_${email}`);
        localStorage.removeItem(`gre_flashcard_mastered_${email}`);
        localStorage.removeItem(`gre_flashcard_review_history_${email}`);
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('gre_flashcards_') && !k.includes('backup') && !k.includes('pending')) {
            localStorage.removeItem(k);
          }
        }
      } catch { /* ignore */ }

      if (oldConfig.rawConfig) {
        cleanOldConfigFlashcards(email, oldConfig.rawConfig);
      }

      console.log('Migration complete.');
      return true;
    } catch (error) {
      console.error('Error migrating localStorage flashcards:', error);
      return false;
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...flashcards];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (card) =>
          card.word.toLowerCase().includes(searchQuery.toLowerCase()) ||
          card.definition_en?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Difficulty filter
    if (filterDifficulty !== 'all') {
      filtered = filtered.filter(
        (card) => card.difficulty_level === filterDifficulty
      );
    }

    // Status filter
    if (filterStatus !== 'all') {
      const now = new Date();
      filtered = filtered.filter((card) => {
        switch (filterStatus) {
          case 'due':
            return new Date(card.nextReviewDate) <= now;
          case 'new':
            return card.reviewCount === 0;
          case 'learning':
            return card.reviewCount > 0 && card.reviewCount < 5;
          case 'mastered':
            return card.reviewCount >= 5 && card.easeFactor >= 2.5;
          case 'fauxAmis': {
            const nw = card.word?.toLowerCase();
            const nuanceData = _nuanceMap[nw];
            if (!nuanceData) return false;
            const combined = `${nuanceData.usage_nuance_fr || ''}\n${nuanceData.usage_nuance_en || ''}`;
            return /faux ami|false friend/i.test(combined);
          }
          default:
            return true;
        }
      });
    }

    // Added date filter
    if (filterAdded !== 'all') {
      const now = new Date();
      const last7Days = new Date(now);
      last7Days.setDate(last7Days.getDate() - 7);
      filtered = filtered.filter((card) => {
        if (!card.addedAt) return false;
        return new Date(card.addedAt) >= last7Days;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'dateAdded':
          return new Date(b.addedAt) - new Date(a.addedAt);
        case 'nextReview':
          return new Date(a.nextReviewDate) - new Date(b.nextReviewDate);
        case 'difficulty':
          const diffOrder = { easy: 1, medium: 2, hard: 3 };
          return diffOrder[a.difficulty_level] - diffOrder[b.difficulty_level];
        case 'alphabetical':
          return a.word.localeCompare(b.word);
        default:
          return 0;
      }
    });

    setFilteredCards(filtered);
  };

  const nuanceLookup = React.useMemo(() => {
    const map = {};
    greWordsData.forEach((w) => {
      if (w.usage_nuance_fr || w.usage_nuance_en) {
        map[w.word.toLowerCase()] = {
          usage_nuance_fr: w.usage_nuance_fr || '',
          usage_nuance_en: w.usage_nuance_en || '',
        };
      }
    });
    return map;
  }, []);

  const greWordLookup = React.useMemo(() => {
    const map = new Map();
    greWordsData.forEach((wordEntry) => {
      const normalizedWord = normalizeFlashcardWord(wordEntry.word);
      if (normalizedWord) {
        map.set(normalizedWord, wordEntry);
      }
    });
    return map;
  }, []);

  const overlayCardWithGreWord = React.useCallback(
    (card) => {
      if (!card) return card;
      const greWord = greWordByKey[normalizeFlashcardWord(card.word)];
      if (!greWord) return card;
      return {
        ...card,
        definition_en: greWord.definition_en || card.definition_en,
        definition_fr: greWord.definition_fr || card.definition_fr,
        example_sentence_en:
          greWord.example_sentence_en || card.example_sentence_en,
        example_sentence_fr:
          greWord.example_sentence_fr || card.example_sentence_fr,
      };
    },
    [greWordByKey]
  );

  const resolveGreWordsForCards = React.useCallback(async (cards) => {
    const words = [
      ...new Set(
        (cards || [])
          .map((card) => card?.word)
          .filter(Boolean)
      ),
    ];
    if (words.length === 0) return;
    try {
      const entries = await apiLookupGreWords(words);
      if (!isMountedRef.current || !entries.length) return;
      setGreWordByKey((current) => {
        const next = { ...current };
        entries.forEach((entry) => {
          const key = normalizeFlashcardWord(entry.word);
          if (key) next[key] = entry;
        });
        return next;
      });
    } catch (error) {
      if (handleAuthFailure(error)) return;
      console.warn('Unable to resolve GREWord definitions:', error);
    }
  }, []);

  const resolveCatalogFields = React.useCallback(
    async (word, fallback = {}) => {
      const normalizedWord = normalizeFlashcardWord(word);
      let greWord = greWordByKey[normalizedWord];
      if (!greWord && normalizedWord) {
        const entries = await apiLookupGreWords([word]);
        greWord = entries[0] || null;
        if (greWord && isMountedRef.current) {
          setGreWordByKey((current) => ({
            ...current,
            [normalizeFlashcardWord(greWord.word)]: greWord,
          }));
        }
      }
      const catalogEntry = greWordLookup.get(normalizedWord);
      return {
        word: greWord?.word || catalogEntry?.word || fallback.word || word,
        definition_en:
          fallback.definition_en ||
          greWord?.definition_en ||
          catalogEntry?.definition_en ||
          '',
        definition_fr:
          fallback.definition_fr ||
          greWord?.definition_fr ||
          catalogEntry?.definition_fr ||
          '',
        example_sentence_en:
          fallback.example_sentence_en ||
          greWord?.example_sentence_en ||
          catalogEntry?.example_en ||
          '',
        example_sentence_fr:
          fallback.example_sentence_fr ||
          greWord?.example_sentence_fr ||
          catalogEntry?.example_fr ||
          '',
        difficulty_level:
          fallback.difficulty_level ||
          mapGreDifficultyToCardDifficulty(
            greWord?.difficulty_level || catalogEntry?.difficulty
          ),
        question: fallback.question || catalogEntry?.question || null,
        options:
          fallback.options ||
          (catalogEntry?.options?.length ? catalogEntry.options : null),
      };
    },
    [greWordByKey, greWordLookup]
  );

  const getNuanceForWord = (word) => {
    if (!word) return null;
    const nuance = nuanceLookup[word.toLowerCase()];
    if (!nuance) return null;
    const combined = `${nuance.usage_nuance_fr || ''}\n${nuance.usage_nuance_en || ''}`;
    const isFalseFriend = /faux ami|false friend/i.test(combined);
    return { ...nuance, isFalseFriend };
  };

  const NuanceCallout = ({ word }) => {
    const [showEN, setShowEN] = useState(false);
    const nuance = getNuanceForWord(word);
    if (!nuance) return null;

    const text = showEN ? nuance.usage_nuance_en : nuance.usage_nuance_fr;
    const fallbackText = showEN ? nuance.usage_nuance_fr : nuance.usage_nuance_en;
    const displayText = (text || fallbackText || '').trim();
    if (!displayText) return null;

    return (
      <div
        className={`flashcard-nuance-callout ${nuance.isFalseFriend ? 'flashcard-nuance-faux-ami' : ''}`}
      >
        <div className="flashcard-nuance-callout-header">
          <div className="flashcard-nuance-callout-title">
            <span className="flashcard-nuance-callout-icon">
              {nuance.isFalseFriend ? '⚠️' : '💡'}
            </span>
            <span>
              {nuance.isFalseFriend ? 'Faux ami / piège' : "Nuance d'usage"}
            </span>
          </div>
          <button
            type="button"
            className="flashcard-nuance-toggle"
            onClick={() => setShowEN((v) => !v)}
            title={showEN ? 'Afficher FR' : 'Afficher EN'}
          >
            {showEN ? 'EN' : 'FR'}
          </button>
        </div>
        <div className="flashcard-nuance-callout-body">
          {renderNuanceInlineMarkdown(displayText)}
        </div>
      </div>
    );
  };

  const deleteCard = async (cardId) => {
    if (window.confirm('Are you sure you want to delete this flashcard?')) {
      try {
        await apiDeleteFlashcard(userEmail, cardId);
        setFlashcards((prev) => prev.filter((card) => card.id !== cardId));
      } catch (error) {
        if (handleAuthFailure(error)) {
          return;
        }
        console.error('Error deleting flashcard:', error);
        setManualAddError('Failed to delete flashcard. Please try again.');
        setTimeout(() => { if (isMountedRef.current) setManualAddError(''); }, 3000);
      }
    }
  };

  const updateCard = async (cardId, updates) => {
    const result = await apiUpdateFlashcard(userEmail, cardId, updates);
    if (result.success && result.flashcard) {
      setFlashcards((prev) =>
        prev.map((card) => (card.id === cardId ? result.flashcard : card))
      );
    } else {
      setFlashcards((prev) =>
        prev.map((card) => (card.id === cardId ? { ...card, ...updates } : card))
      );
    }
  };

  const handleCardReviewed = async (word, isCorrect, quality) => {
    const normalizedWord = normalizeFlashcardWord(word);
    const card = flashcards.find(
      (c) => normalizeFlashcardWord(c.word) === normalizedWord
    );
    if (!card) return;

    const { easeFactor, interval, nextReviewDate } = calculateNextReview(
      card.easeFactor,
      card.interval,
      quality
    );

    const newReviewCount = card.reviewCount + 1;
    const nowIso = new Date().toISOString();

    try {
      await updateCard(card.id, {
        easeFactor,
        interval,
        nextReviewDate,
        reviewCount: newReviewCount,
        lastReviewed: nowIso,
      });
      apiRecordReviewEvent(userEmail, isCorrect);
    } catch {
      // card update failed — don't record review event
    }
  };

  const getReviewHistory = () => reviewHistory;

  const getAnalyticsData = () => {
    const history = getReviewHistory();
    const now = new Date();
    const last30 = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().slice(0, 10);
      const entry = history.find((h) => h.date === dateStr);
      last30.push({
        date: dateStr,
        label: d.toLocaleDateString('en', { month: 'short', day: 'numeric' }),
        cardsReviewed: entry?.cardsReviewed || 0,
        correct: entry?.correct || 0,
        incorrect: entry?.incorrect || 0,
      });
    }

    const totalCards = flashcards.length;
    const reviewedCards = flashcards.filter((c) => c.reviewCount > 0);
    const masteredCards = flashcards.filter(
      (c) => c.reviewCount >= 5 && c.easeFactor >= 2.5
    );
    const easyCards = flashcards.filter((c) => c.difficulty_level === 'easy');
    const mediumCards = flashcards.filter((c) => c.difficulty_level === 'medium');
    const hardCards = flashcards.filter((c) => c.difficulty_level === 'hard');

    const retentionByDifficulty = (cards) => {
      const reviewed = cards.filter((c) => c.reviewCount > 0);
      if (reviewed.length === 0) return 0;
      const mastered = reviewed.filter(
        (c) => c.reviewCount >= 5 && c.easeFactor >= 2.5
      );
      return Math.round((mastered.length / reviewed.length) * 100);
    };

    const overallRetention =
      reviewedCards.length > 0
        ? Math.round((masteredCards.length / reviewedCards.length) * 100)
        : 0;

    const weakWords = [...flashcards]
      .filter((c) => c.reviewCount > 0)
      .sort((a, b) => a.easeFactor - b.easeFactor)
      .slice(0, 20);

    const totalReviewedLast7 = last30
      .slice(-7)
      .reduce((s, d) => s + d.cardsReviewed, 0);
    const avgReviewsPerDay = Math.round(totalReviewedLast7 / 7);

    const addedLast7 = flashcards.filter((c) => {
      if (!c.addedAt) return false;
      const d = new Date(c.addedAt);
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return d >= weekAgo;
    }).length;
    const avgAddedPerDay = Math.round(addedLast7 / 7);

    const TARGET_WORDS = 800;
    const readinessScore = Math.min(
      Math.round((masteredCards.length / TARGET_WORDS) * 100),
      100
    );

    const masteredPerWeek =
      history.length > 0
        ? Math.max(
            1,
            Math.round(
              (masteredCards.length / Math.max(history.length, 7)) * 7
            )
          )
        : 0;
    const remaining = TARGET_WORDS - masteredCards.length;
    const daysToTarget =
      masteredPerWeek > 0
        ? Math.round((remaining / masteredPerWeek) * 7)
        : null;

    const dueCards = flashcards.filter(
      (c) => new Date(c.nextReviewDate) <= now
    );
    const daysSinceLastReview = (() => {
      const lastEntry = [...history].reverse().find((h) => h.cardsReviewed > 0);
      if (!lastEntry) return null;
      const diff = Math.floor(
        (now - new Date(lastEntry.date)) / (1000 * 60 * 60 * 24)
      );
      return diff;
    })();

    return {
      last30,
      totalCards,
      reviewedCards: reviewedCards.length,
      masteredCards: masteredCards.length,
      overallRetention,
      retentionEasy: retentionByDifficulty(easyCards),
      retentionMedium: retentionByDifficulty(mediumCards),
      retentionHard: retentionByDifficulty(hardCards),
      weakWords,
      avgReviewsPerDay,
      avgAddedPerDay,
      readinessScore,
      daysToTarget,
      dueCards: dueCards.length,
      daysSinceLastReview,
      TARGET_WORDS,
    };
  };

  const resetManualAddForm = () => {
    setManualCardDraft(createManualCardDraft());
    setManualAddError('');
  };

  const showActionFeedback = (type, message, duration = 3000) => {
    if (actionFeedbackTimeoutRef.current) {
      clearTimeout(actionFeedbackTimeoutRef.current);
    }

    setActionFeedback({ type, message });

    if (duration > 0) {
      actionFeedbackTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current) {
          setActionFeedback({ type: '', message: '' });
        }
      }, duration);
    }
  };

  const openManualAddModal = () => {
    resetManualAddForm();
    setActionFeedback({ type: '', message: '' });
    setShowManualAddModal(true);
  };

  const closeManualAddModal = () => {
    setShowManualAddModal(false);
    setManualAddError('');
  };

  const handleManualDraftChange = (field, value) => {
    setManualCardDraft((current) => ({
      ...current,
      [field]: value,
    }));
    if (manualAddError) {
      setManualAddError('');
    }
  };

  const addManualFlashcard = async () => {
    const normalizedWord = normalizeFlashcardWord(manualCardDraft.word);

    if (!normalizedWord) {
      setManualAddError('Please enter a word to add.');
      return;
    }

    if (
      flashcards.some(
        (card) => normalizeFlashcardWord(card.word) === normalizedWord
      )
    ) {
      setManualAddError('This word is already in your flashcard book.');
      return;
    }

    setIsAddingManualCard(true);

    try {
      const nowIso = new Date().toISOString();
      const resolved = await resolveCatalogFields(manualCardDraft.word, {
        word: manualCardDraft.word.trim(),
        definition_en: manualCardDraft.definition_en.trim(),
        definition_fr: manualCardDraft.definition_fr.trim(),
        example_sentence_en: manualCardDraft.example_sentence_en.trim(),
        example_sentence_fr: manualCardDraft.example_sentence_fr.trim(),
        difficulty_level: manualCardDraft.difficulty_level,
      });
      const cardData = {
        ...resolved,
        emoticon: manualCardDraft.emoticon.trim(),
        personalNote: manualCardDraft.personalNote.trim(),
        addedAt: nowIso,
      };

      const result = await apiAddFlashcard(userEmail, cardData);
      if (result.success === false) {
        setManualAddError(result.message || 'Word already in flashcards.');
        return;
      }
      if (result.flashcard) {
        setFlashcards((prev) => [result.flashcard, ...prev]);
      }
      closeManualAddModal();
      resetManualAddForm();
      showActionFeedback('success', `"${cardData.word}" was added.`);
    } catch (error) {
      if (handleAuthFailure(error)) {
        return;
      }
      console.error('Error adding manual flashcard:', error);
      setManualAddError('Unable to save this flashcard right now.');
    } finally {
      if (isMountedRef.current) {
        setIsAddingManualCard(false);
      }
    }
  };

  const existingWordsSet = React.useMemo(() => {
    const s = new Set();
    flashcards.forEach((c) => s.add(normalizeFlashcardWord(c.word)));
    return s;
  }, [flashcards]);

  const browseFilteredWords = React.useMemo(() => {
    let words = greWordsData;
    if (browseSearch) {
      const q = browseSearch.toLowerCase();
      words = words.filter(
        (w) =>
          w.word.toLowerCase().includes(q) ||
          (w.definition_en || '').toLowerCase().includes(q)
      );
    }
    if (browseDifficulty !== 'all') {
      words = words.filter(
        (w) => (w.difficulty || '').toLowerCase() === browseDifficulty
      );
    }
    if (browseLetter !== 'all') {
      words = words.filter(
        (w) => w.word.charAt(0).toUpperCase() === browseLetter
      );
    }
    return words;
  }, [browseSearch, browseDifficulty, browseLetter]);

  const openBrowseLibrary = () => {
    setBrowseSearch('');
    setBrowseDifficulty('all');
    setBrowseLetter('all');
    setBrowseSelected(new Set());
    setShowBrowseLibrary(true);
  };

  const toggleBrowseSelect = (word) => {
    setBrowseSelected((prev) => {
      const next = new Set(prev);
      if (next.has(word)) next.delete(word);
      else next.add(word);
      return next;
    });
  };

  const addBrowseSelectedWords = async () => {
    if (browseSelected.size === 0) return;
    setIsAddingBrowse(true);
    try {
      const nowIso = new Date().toISOString();
      const newCards = [];
      for (const word of browseSelected) {
        const nw = normalizeFlashcardWord(word);
        if (existingWordsSet.has(nw)) continue;
        const resolved = await resolveCatalogFields(word);
        newCards.push({
          ...resolved,
          emoticon: '',
          personalNote: '',
          addedAt: nowIso,
        });
      }
      if (newCards.length > 0) {
        await apiBulkAddFlashcards(userEmail, newCards);
        const refreshed = await apiFetchFlashcards(userEmail);
        if (isMountedRef.current) setFlashcards(refreshed);
        resolveGreWordsForCards(refreshed);
        showActionFeedback(
          'success',
          `${newCards.length} word${newCards.length > 1 ? 's' : ''} added.`
        );
      }
      setShowBrowseLibrary(false);
    } catch (error) {
      if (handleAuthFailure(error)) {
        return;
      }
      console.error('Error adding browse words:', error);
      showActionFeedback(
        'error',
        'Failed to add selected words. Please try again.',
        4000
      );
    } finally {
      if (isMountedRef.current) setIsAddingBrowse(false);
    }
  };

  const parseImportedFlashcards = (fileText) => {
    const delimiter = detectCsvDelimiter(fileText);
    const rows = parseDelimitedRows(fileText, delimiter);

    if (rows.length < 2) {
      throw new Error(
        'Your CSV needs a header row and at least one flashcard row.'
      );
    }

    const [headerRow, ...dataRows] = rows;
    const columnMap = {};

    headerRow.forEach((header, index) => {
      const field = resolveCsvColumnKey(header);
      if (field && columnMap[field] == null) {
        columnMap[field] = index;
      }
    });

    if (columnMap.word == null) {
      throw new Error(
        'Your CSV must include a Word or Mot column.'
      );
    }

    const seenImportedWords = new Set();
    const cards = [];
    let skippedEmptyRows = 0;
    let skippedDuplicates = 0;

    dataRows.forEach((row) => {
      const rawWord = getCsvCell(row, columnMap, 'word');
      const normalizedWord = normalizeFlashcardWord(rawWord);

      if (!normalizedWord) {
        skippedEmptyRows += 1;
        return;
      }

      if (
        existingWordsSet.has(normalizedWord) ||
        seenImportedWords.has(normalizedWord)
      ) {
        skippedDuplicates += 1;
        return;
      }

      const catalogEntry = greWordLookup.get(normalizedWord);
      const greWord = greWordByKey[normalizedWord];
      const importedOptions = parseCsvOptions(getCsvCell(row, columnMap, 'options'));

      cards.push({
        word: greWord?.word || catalogEntry?.word || rawWord.trim(),
        definition_en:
          getCsvCell(row, columnMap, 'definition_en') ||
          greWord?.definition_en ||
          catalogEntry?.definition_en ||
          '',
        definition_fr:
          getCsvCell(row, columnMap, 'definition_fr') ||
          greWord?.definition_fr ||
          catalogEntry?.definition_fr ||
          '',
        example_sentence_en:
          getCsvCell(row, columnMap, 'example_sentence_en') ||
          greWord?.example_sentence_en ||
          catalogEntry?.example_en ||
          '',
        example_sentence_fr:
          getCsvCell(row, columnMap, 'example_sentence_fr') ||
          greWord?.example_sentence_fr ||
          catalogEntry?.example_fr ||
          '',
        difficulty_level:
          mapGreDifficultyToCardDifficulty(
            getCsvCell(row, columnMap, 'difficulty_level') ||
              greWord?.difficulty_level ||
              catalogEntry?.difficulty
          ) || 'medium',
        emoticon: getCsvCell(row, columnMap, 'emoticon'),
        personalNote: getCsvCell(row, columnMap, 'personalNote'),
        question:
          getCsvCell(row, columnMap, 'question') || catalogEntry?.question || null,
        options:
          importedOptions ||
          (catalogEntry?.options?.length ? catalogEntry.options : null),
        addedAt: new Date().toISOString(),
      });

      seenImportedWords.add(normalizedWord);
    });

    return {
      cards,
      skippedEmptyRows,
      skippedDuplicates,
      totalRows: dataRows.length,
    };
  };

  const openCsvImportPicker = () => {
    if (!isImportingCsv) {
      setShowCsvFormatInfo(false);
      csvImportInputRef.current?.click();
    }
  };

  const closeCsvImportPreview = () => {
    if (isImportingCsv) return;
    setCsvImportPreview(null);
  };

  const handleCsvFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    setIsImportingCsv(true);
    setActionFeedback({ type: '', message: '' });

    try {
      const fileText = await file.text();
      const importSummary = parseImportedFlashcards(fileText);

      if (importSummary.cards.length === 0) {
        showActionFeedback(
          'error',
          'No new flashcards were found in that CSV. Check for empty rows or duplicates.',
          5000
        );
        return;
      }

      setCsvImportPreview({
        fileName: file.name,
        ...importSummary,
      });
    } catch (error) {
      if (handleAuthFailure(error)) {
        return;
      }
      console.error('Error importing flashcards from CSV:', error);
      showActionFeedback(
        'error',
        error?.message || 'Failed to import that CSV file.',
        5000
      );
    } finally {
      if (isMountedRef.current) {
        setIsImportingCsv(false);
      }
    }
  };

  const confirmCsvImport = async () => {
    if (!csvImportPreview || csvImportPreview.cards.length === 0) return;

    setIsImportingCsv(true);
    setActionFeedback({ type: '', message: '' });

    try {
      for (
        let index = 0;
        index < csvImportPreview.cards.length;
        index += CSV_IMPORT_BATCH_SIZE
      ) {
        const chunk = csvImportPreview.cards.slice(
          index,
          index + CSV_IMPORT_BATCH_SIZE
        );
        await apiBulkAddFlashcards(userEmail, chunk);
      }

      const refreshed = await apiFetchFlashcards(userEmail);
      if (isMountedRef.current) {
        setFlashcards(refreshed);
      }

      const previousWords = new Set(
        flashcards.map((card) => normalizeFlashcardWord(card.word))
      );
      const actualAddedCount = refreshed.reduce((count, card) => {
        const normalizedWord = normalizeFlashcardWord(card.word);
        return previousWords.has(normalizedWord) ? count : count + 1;
      }, 0);

      const skippedCount =
        csvImportPreview.skippedDuplicates + csvImportPreview.skippedEmptyRows;
      const summaryParts = [
        `${actualAddedCount || csvImportPreview.cards.length} flashcard${
          (actualAddedCount || csvImportPreview.cards.length) > 1 ? 's' : ''
        } imported from ${csvImportPreview.fileName}.`,
      ];

      if (skippedCount > 0) {
        summaryParts.push(
          `${skippedCount} row${skippedCount > 1 ? 's were' : ' was'} skipped (empty or duplicate).`
        );
      }

      setCsvImportPreview(null);
      showActionFeedback('success', summaryParts.join(' '), 5000);
    } catch (error) {
      if (handleAuthFailure(error)) {
        return;
      }
      console.error('Error importing flashcards from CSV:', error);
      showActionFeedback(
        'error',
        error?.message || 'Failed to import that CSV file.',
        5000
      );
    } finally {
      if (isMountedRef.current) {
        setIsImportingCsv(false);
      }
    }
  };

  const downloadFlashcardsPdf = async () => {
    if (flashcards.length === 0) return;

    setIsExportingPdf(true);

    try {
      const { jsPDF } = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');
      const hasScopedView =
        filteredCards.length > 0 && filteredCards.length !== flashcards.length;
      const cardsToExport =
        filteredCards.length > 0 ? filteredCards : flashcards;

      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
      });

      const liwazaColors = {
        primary: [38, 39, 48],
        onPrimary: [255, 255, 255],
        secondary: [98, 91, 113],
        surface: [248, 241, 246],
        primaryContainer: [220, 218, 245],
        onPrimaryContainer: [74, 68, 88],
        border: [224, 224, 224],
        text: [29, 27, 32],
        mutedText: [74, 68, 88],
      };

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      const exportedAt = new Date().toLocaleDateString();
      const tableRows = cardsToExport.map((card, index) => [
        index + 1,
        card.word || '',
        card.difficulty_level || 'medium',
        card.definition_en || '',
        card.definition_fr || '',
        card.example_sentence_en || '',
        card.example_sentence_fr || '',
        card.personalNote || card.personal_note || '',
      ]);

      autoTable(doc, {
        startY: 110,
        margin: { top: 110, right: margin, bottom: 46, left: margin },
        tableWidth: pageWidth - margin * 2,
        head: [
          [
            '#',
            'Word',
            'Difficulty',
            'Definition (EN)',
            'Definition (FR)',
            'Example (EN)',
            'Example (FR)',
            'Note',
          ],
        ],
        body: tableRows,
        theme: 'grid',
        styles: {
          font: 'helvetica',
          fontSize: 8,
          cellPadding: 5,
          lineColor: liwazaColors.border,
          lineWidth: 0.6,
          textColor: liwazaColors.text,
          overflow: 'linebreak',
          valign: 'top',
          fillColor: liwazaColors.surface,
        },
        headStyles: {
          fillColor: liwazaColors.secondary,
          textColor: liwazaColors.onPrimary,
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: liwazaColors.primaryContainer,
        },
        columnStyles: {
          0: { cellWidth: 28, halign: 'center' },
          1: { cellWidth: 84, fontStyle: 'bold' },
          2: { cellWidth: 56, halign: 'center' },
          3: { cellWidth: 120 },
          4: { cellWidth: 120 },
          5: { cellWidth: 150 },
          6: { cellWidth: 150 },
          7: { cellWidth: 94 },
        },
        didDrawPage: (data) => {
          doc.setFillColor(...liwazaColors.primary);
          doc.rect(0, 0, pageWidth, 64, 'F');

          doc.setTextColor(...liwazaColors.onPrimary);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(20);
          doc.text('Liwaza Flashcards Export', margin, 28);

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text('Document generated from Liwaza | liwaza.org', margin, 46);

          doc.setTextColor(...liwazaColors.mutedText);
          doc.setFontSize(9.5);
          doc.text(
            `${cardsToExport.length} word${cardsToExport.length > 1 ? 's' : ''} exported on ${exportedAt}`,
            margin,
            84
          );

          if (hasScopedView) {
            doc.text(
              'This PDF reflects your current filtered flashcard view.',
              margin,
              98
            );
          }

          doc.setDrawColor(...liwazaColors.border);
          doc.line(margin, pageHeight - 30, pageWidth - margin, pageHeight - 30);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.text('Source: liwaza.org', margin, pageHeight - 14);
        },
      });

      const totalPages = doc.getNumberOfPages();
      for (let page = 1; page <= totalPages; page += 1) {
        doc.setPage(page);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(
          `Page ${page} of ${totalPages}`,
          pageWidth - margin,
          pageHeight - 14,
          { align: 'right' }
        );
      }

      const dateStamp = new Date().toISOString().slice(0, 10);
      doc.save(`liwaza-flashcards-${dateStamp}.pdf`);
      showActionFeedback(
        'success',
        `Downloaded ${cardsToExport.length} flashcard${
          cardsToExport.length > 1 ? 's' : ''
        } as a PDF.`,
        4000
      );
    } catch (error) {
      console.error('Error exporting flashcards to PDF:', error);
      showActionFeedback(
        'error',
        'Unable to generate the PDF right now. Please try again.',
        5000
      );
    } finally {
      if (isMountedRef.current) {
        setIsExportingPdf(false);
      }
    }
  };

  const startReview = () => {
    if (flashcards.length === 0) {
      alert('No words yet. Add a few flashcards first!');
      return;
    }

    setShowReviewModeSelector(true);
  };

  const startQuizMode = () => {
    setShowReviewModeSelector(false);
    setShowWordsChallenge(true);
  };

  const startFlashcardMode = () => {
    setShowReviewModeSelector(false);

    const now = new Date();
    let cards = flashcards.filter(
      (card) => new Date(card.nextReviewDate) <= now
    );
    if (cards.length === 0) {
      const last7Days = new Date(now);
      last7Days.setDate(last7Days.getDate() - 7);
      cards = flashcards.filter((card) => {
        if (!card.addedAt) return false;
        return new Date(card.addedAt) >= last7Days;
      });
    }
    if (cards.length === 0) cards = [...flashcards];

    cards.sort(() => Math.random() - 0.5);
    setReviewCards(cards);
    setCurrentReviewIndex(0);
    setShowAnswer(false);
    setIsReviewMode(true);
  };

  const rateCard = async (quality) => {
    const card = reviewCards[currentReviewIndex];
    const { easeFactor, interval, nextReviewDate } = calculateNextReview(
      card.easeFactor,
      card.interval,
      quality
    );

    const newReviewCount = card.reviewCount + 1;
    const nowIso = new Date().toISOString();

    try {
      await updateCard(card.id, {
        easeFactor,
        interval,
        nextReviewDate,
        reviewCount: newReviewCount,
        lastReviewed: nowIso,
      });
      apiRecordReviewEvent(userEmail, quality >= 3);
    } catch {
      // card update failed — don't record review event
    }

    // Move to next card
    if (currentReviewIndex < reviewCards.length - 1) {
      setCurrentReviewIndex(currentReviewIndex + 1);
      setShowAnswer(false);
    } else {
      setIsReviewMode(false);
      alert(
        `Review complete! You reviewed ${reviewCards.length} card${reviewCards.length > 1 ? 's' : ''}.`
      );
    }
  };

  const getCardStatus = (card) => {
    if (card.reviewCount === 0) return 'new';
    if (card.reviewCount < 5) return 'learning';
    if (card.easeFactor >= 2.5) return 'mastered';
    return 'learning';
  };

  const getDueCardsCount = () => {
    const now = new Date();
    return flashcards.filter((card) => new Date(card.nextReviewDate) <= now)
      .length;
  };

  const getThisWeekCardsCount = () => {
    const now = new Date();
    const last7Days = new Date(now);
    last7Days.setDate(last7Days.getDate() - 7);
    return flashcards.filter((card) => {
      if (!card.addedAt) return false;
      return new Date(card.addedAt) >= last7Days;
    }).length;
  };

  const getDueCardsForChallenge = () => {
    const now = new Date();
    const dueCards = flashcards.filter(
      (card) => new Date(card.nextReviewDate) <= now
    );

    // Convert flashcard format to GREWordsChallenge format
    return dueCards.map((rawCard) => {
      const card = overlayCardWithGreWord(rawCard);
      const baseCard = {
        word: card.word,
        definition: card.definition_en || card.definition,
        definition_en: card.definition_en || card.definition,
        definition_fr:
          card.definition_fr || card.definition_en || card.definition,
        example: card.example_en || card.example,
        example_en: card.example_en || card.example,
        example_fr: card.example_fr || card.example_en || card.example,
        difficulty: card.difficulty || 'medium',
        addedAt: card.addedAt,
        nextReviewDate: card.nextReviewDate,
        reviewCount: card.reviewCount,
        easeFactor: card.easeFactor,
        difficulty_level: card.difficulty_level,
      };

      // Include riddle question and options only if they exist
      if (card.question && card.options && card.options.length > 0) {
        baseCard.question = card.question;
        baseCard.options = card.options;
      }

      const nuance = getNuanceForWord(card.word);
      if (nuance) {
        baseCard.usage_nuance_fr = nuance.usage_nuance_fr;
        baseCard.usage_nuance_en = nuance.usage_nuance_en;
      }

      return baseCard;
    });
  };

  const getAllCardsForOptions = () => {
    // Convert ALL flashcards to GREWordsChallenge format
    // This provides a larger pool for generating wrong answer options
    return flashcards.map((rawCard) => {
      const card = overlayCardWithGreWord(rawCard);
      const baseCard = {
        word: card.word,
        definition: card.definition_en || card.definition,
        definition_en: card.definition_en || card.definition,
        definition_fr:
          card.definition_fr || card.definition_en || card.definition,
        example: card.example_en || card.example,
        example_en: card.example_en || card.example,
        example_fr: card.example_fr || card.example_en || card.example,
        difficulty: card.difficulty || 'medium',
        addedAt: card.addedAt,
        nextReviewDate: card.nextReviewDate,
        reviewCount: card.reviewCount,
        easeFactor: card.easeFactor,
        difficulty_level: card.difficulty_level,
      };

      // Include riddle question and options only if they exist
      if (card.question && card.options && card.options.length > 0) {
        baseCard.question = card.question;
        baseCard.options = card.options;
      }

      const nuance = getNuanceForWord(card.word);
      if (nuance) {
        baseCard.usage_nuance_fr = nuance.usage_nuance_fr;
        baseCard.usage_nuance_en = nuance.usage_nuance_en;
      }

      return baseCard;
    });
  };

  const getFilteredCardsForChallenge = () => {
    return filteredCards.map((rawCard) => {
      const card = overlayCardWithGreWord(rawCard);
      const baseCard = {
        word: card.word,
        definition: card.definition_en || card.definition,
        definition_en: card.definition_en || card.definition,
        definition_fr: card.definition_fr || card.definition_en || card.definition,
        example: card.example_en || card.example,
        example_en: card.example_en || card.example,
        example_fr: card.example_fr || card.example_en || card.example,
        difficulty: card.difficulty || 'medium',
        addedAt: card.addedAt,
        nextReviewDate: card.nextReviewDate,
        reviewCount: card.reviewCount,
        easeFactor: card.easeFactor,
        difficulty_level: card.difficulty_level,
      };

      if (card.question && card.options && card.options.length > 0) {
        baseCard.question = card.question;
        baseCard.options = card.options;
      }

      const nuance = getNuanceForWord(card.word);
      if (nuance) {
        baseCard.usage_nuance_fr = nuance.usage_nuance_fr;
        baseCard.usage_nuance_en = nuance.usage_nuance_en;
      }
      return baseCard;
    });
  };

  const getPlayableWordsCount = () => {
    const dueCount = getDueCardsCount();
    return dueCount > 0 ? dueCount : flashcards.length;
  };

  const renderStats = () => {
    const totalCards = flashcards.length;
    const dueCards = getDueCardsCount();
    const newCards = flashcards.filter((card) => card.reviewCount === 0).length;
    const learningCards = flashcards.filter(
      (card) => card.reviewCount > 0 && (card.reviewCount < 5 || card.easeFactor < 2.5)
    ).length;
    const masteredCards = flashcards.filter(
      (card) => card.reviewCount >= 5 && card.easeFactor >= 2.5
    ).length;

    return (
      <div className="flashcard-stats">
        <div className="flashcard-stat-card flashcard-stat-total">
          <FiBookmark className="flashcard-stat-icon" />
          <div className="flashcard-stat-content">
            <div className="flashcard-stat-value">{totalCards}</div>
            <div className="flashcard-stat-label">Total Words</div>
          </div>
        </div>

        <div className="flashcard-stat-card flashcard-stat-due">
          <FiClock className="flashcard-stat-icon" />
          <div className="flashcard-stat-content">
            <div className="flashcard-stat-value">{dueCards}</div>
            <div className="flashcard-stat-label">Due for Review</div>
          </div>
        </div>

        <div className="flashcard-stat-card flashcard-stat-new">
          <FiPlus className="flashcard-stat-icon" />
          <div className="flashcard-stat-content">
            <div className="flashcard-stat-value">{newCards}</div>
            <div className="flashcard-stat-label">New</div>
          </div>
        </div>

        <div className="flashcard-stat-card flashcard-stat-learning">
          <FiRotateCw className="flashcard-stat-icon" />
          <div className="flashcard-stat-content">
            <div className="flashcard-stat-value">{learningCards}</div>
            <div className="flashcard-stat-label">Learning</div>
          </div>
        </div>

        <div className="flashcard-stat-card flashcard-stat-mastered">
          <FiStar className="flashcard-stat-icon" />
          <div className="flashcard-stat-content">
            <div className="flashcard-stat-value">{masteredCards}</div>
            <div className="flashcard-stat-label">Mastered</div>
          </div>
        </div>
      </div>
    );
  };

  const renderFilters = () => (
    <div className={`flashcard-filters ${showFilters ? 'show' : ''}`}>
      <div className="flashcard-filter-chip-group">
        <div className="flashcard-filter-chip">
          <label>Difficulty</label>
          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
          >
            <option value="all">All Levels</option>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>

        <div className="flashcard-filter-chip">
          <label>Status</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Words</option>
            <option value="due">Due for Review</option>
            <option value="new">New Words</option>
            <option value="learning">Learning</option>
            <option value="mastered">Mastered</option>
            <option value="fauxAmis">⚠️ Faux Amis</option>
          </select>
        </div>

        <div className="flashcard-filter-chip">
          <label>Added</label>
          <select value={filterAdded} onChange={(e) => setFilterAdded(e.target.value)}>
            <option value="all">All time</option>
            <option value="last7d">Last 7 days</option>
          </select>
        </div>

        <div className="flashcard-filter-chip">
          <label>Sort</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="dateAdded">Date Added</option>
            <option value="nextReview">Next Review</option>
            <option value="difficulty">Difficulty</option>
            <option value="alphabetical">A → Z</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderCardDetails = (rawCard) => {
    const card = overlayCardWithGreWord(rawCard);
    return (
    <div className="flashcard-detail-modal">
      <div
        className="flashcard-modal-overlay"
        onClick={() => setShowInfo(null)}
      />
      <div className="flashcard-modal-content">
        <div className="flashcard-modal-header">
          <div className="flashcard-word-header">
            {card.emoticon && (
              <span className="flashcard-card-emoticon">{card.emoticon}</span>
            )}
            <h2>{card.word}</h2>
            <button
              onClick={() => speakWord(card.word)}
              className="flashcard-pronunciation-btn"
            >
              <FiVolume2 />
            </button>
          </div>
          <button
            onClick={() => setShowInfo(null)}
            className="flashcard-close-modal-btn"
          >
            ×
          </button>
        </div>

        <div className="flashcard-modal-body">
          <div className="flashcard-card-info-section">
            <span
              className={`flashcard-difficulty-badge flashcard-difficulty-${card.difficulty_level}`}
            >
              {card.difficulty_level}
            </span>
            <span
              className={`flashcard-status-badge flashcard-status-${getCardStatus(card)}`}
            >
              {getCardStatus(card)}
            </span>
          </div>

          <div className="flashcard-definition-section">
            <h3>English Definition</h3>
            <p>{card.definition_en}</p>
          </div>

          <NuanceCallout word={card.word} />

          {card.definition_fr && (
            <div className="flashcard-definition-section">
              <h3>French Definition</h3>
              <p className="flashcard-french-text">{card.definition_fr}</p>
            </div>
          )}

          {card.example_sentence_en && (
            <div className="flashcard-example-section">
              <h3>Example (EN)</h3>
              <p className="flashcard-example-text">
                "{card.example_sentence_en}"
              </p>
            </div>
          )}

          {card.example_sentence_fr && (
            <div className="flashcard-example-section">
              <h3>Example (FR)</h3>
              <p className="flashcard-example-text flashcard-french-text">
                "{card.example_sentence_fr}"
              </p>
            </div>
          )}

          {card.personalNote && (
            <div className="flashcard-note-section">
              <h3>Personal Note</h3>
              <p>{card.personalNote}</p>
            </div>
          )}
        </div>

        <div className="flashcard-modal-actions">
          <button
            onClick={() => setShowInfo(null)}
            className="flashcard-cancel-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    );
  };

  const renderAnalyticsModal = () => {
    if (!showAnalytics) return null;

    const card = showAnalytics;
    const daysSinceAdded = Math.floor(
      (new Date() - new Date(card.addedAt)) / (1000 * 60 * 60 * 24)
    );
    const daysUntilReview = Math.ceil(
      (new Date(card.nextReviewDate) - new Date()) / (1000 * 60 * 60 * 24)
    );

    return (
      <div className="flashcard-analytics-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={() => setShowAnalytics(null)}
        />
        <div className="flashcard-modal-content">
          <div className="flashcard-modal-header">
            <h2>
              <FiTrendingUp /> Analytics
            </h2>
            <button
              onClick={() => setShowAnalytics(null)}
              className="flashcard-close-modal-btn"
            >
              ×
            </button>
          </div>

          <div className="flashcard-modal-body">
            <div className="flashcard-analytics-word">
              {card.emoticon && <span>{card.emoticon}</span>}
              <h3>{card.word}</h3>
            </div>

            <div className="flashcard-analytics-grid">
              <div className="flashcard-analytics-item">
                <div className="flashcard-analytics-icon">
                  <FiRotateCw />
                </div>
                <div className="flashcard-analytics-content">
                  <div className="flashcard-analytics-value">
                    {card.reviewCount}
                  </div>
                  <div className="flashcard-analytics-label">
                    Times Reviewed
                  </div>
                </div>
              </div>

              <div className="flashcard-analytics-item">
                <div className="flashcard-analytics-icon">
                  <FiCalendar />
                </div>
                <div className="flashcard-analytics-content">
                  <div className="flashcard-analytics-value">
                    {daysUntilReview > 0 ? `${daysUntilReview}d` : 'Due'}
                  </div>
                  <div className="flashcard-analytics-label">Next Review</div>
                </div>
              </div>

              <div className="flashcard-analytics-item">
                <div className="flashcard-analytics-icon">
                  <FiTrendingUp />
                </div>
                <div className="flashcard-analytics-content">
                  <div className="flashcard-analytics-value">
                    {card.easeFactor.toFixed(2)}
                  </div>
                  <div className="flashcard-analytics-label">Ease Factor</div>
                </div>
              </div>

              <div className="flashcard-analytics-item">
                <div className="flashcard-analytics-icon">
                  <FiClock />
                </div>
                <div className="flashcard-analytics-content">
                  <div className="flashcard-analytics-value">
                    {card.interval}d
                  </div>
                  <div className="flashcard-analytics-label">
                    Current Interval
                  </div>
                </div>
              </div>

              <div className="flashcard-analytics-item">
                <div className="flashcard-analytics-icon">
                  <FiCalendar />
                </div>
                <div className="flashcard-analytics-content">
                  <div className="flashcard-analytics-value">
                    {daysSinceAdded}d
                  </div>
                  <div className="flashcard-analytics-label">Days in Deck</div>
                </div>
              </div>

              <div className="flashcard-analytics-item">
                <div className="flashcard-analytics-icon">
                  <FiStar />
                </div>
                <div className="flashcard-analytics-content">
                  <div className="flashcard-analytics-value">
                    {getCardStatus(card)}
                  </div>
                  <div className="flashcard-analytics-label">Status</div>
                </div>
              </div>
            </div>

            {card.lastReviewed && (
              <div className="flashcard-analytics-footer">
                <FiClock />
                <span>
                  Last reviewed:{' '}
                  {new Date(card.lastReviewed).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          <div className="flashcard-modal-actions">
            <button
              onClick={() => setShowAnalytics(null)}
              className="flashcard-cancel-btn"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!editingCard) return null;

    const quickEmojis = ['😊', '🔥', '💡', '⚡', '🎯', '💪', '🌟', '🚀', '🧠', '💎', '🎓', '⚔️'];

    return (
      <div className="flashcard-edit-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={() => setEditingCard(null)}
        />
        <div className="flashcard-modal-content flashcard-edit-modal-content">
          <div className="flashcard-edit-header">
            <div className="flashcard-edit-header-left">
              <div className="flashcard-edit-word-display">
                {editingCard.emoticon && (
                  <span className="flashcard-edit-current-emoji">{editingCard.emoticon}</span>
                )}
                <h2>{editingCard.word}</h2>
              </div>
              {editingCard.definition_en && (
                <p className="flashcard-edit-definition">{editingCard.definition_en}</p>
              )}
            </div>
            <button
              onClick={() => setEditingCard(null)}
              className="flashcard-close-modal-btn"
            >
              ×
            </button>
          </div>

          <div className="flashcard-modal-body">
            <div className="flashcard-edit-section">
              <label className="flashcard-edit-section-label">
                Emoji
                {!editingCard.emoticon && (
                  <span className="flashcard-label-badge">Boost Memory!</span>
                )}
              </label>
              <div className="flashcard-emoji-picker-row">
                <input
                  type="text"
                  value={editingCard.emoticon || ''}
                  onChange={(e) =>
                    setEditingCard({ ...editingCard, emoticon: e.target.value })
                  }
                  placeholder="Type or pick →"
                  maxLength={2}
                  className="flashcard-emoji-input-compact"
                />
                <div className="flashcard-emoji-quick-picks">
                  {quickEmojis.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className={`flashcard-emoji-pick-btn ${editingCard.emoticon === emoji ? 'active' : ''}`}
                      onClick={() =>
                        setEditingCard({ ...editingCard, emoticon: emoji })
                      }
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <NuanceCallout word={editingCard.word} />

            <div className="flashcard-edit-section">
              <label className="flashcard-edit-section-label">Personal Note</label>
              <textarea
                value={editingCard.personalNote || ''}
                onChange={(e) =>
                  setEditingCard({
                    ...editingCard,
                    personalNote: e.target.value,
                  })
                }
                placeholder="Add your own notes, mnemonics, or memory tricks..."
                rows={3}
                className="flashcard-edit-textarea"
              />
            </div>

            <div className="flashcard-edit-section">
              <label className="flashcard-edit-section-label">Difficulty</label>
              <div className="flashcard-difficulty-chips">
                {[
                  { value: 'easy', label: 'Easy', color: 'green' },
                  { value: 'medium', label: 'Medium', color: 'amber' },
                  { value: 'hard', label: 'Hard', color: 'red' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`flashcard-difficulty-chip flashcard-difficulty-chip--${opt.color} ${
                      editingCard.difficulty_level === opt.value ? 'active' : ''
                    }`}
                    onClick={() =>
                      setEditingCard({
                        ...editingCard,
                        difficulty_level: opt.value,
                      })
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flashcard-edit-actions">
            <button
              onClick={() => setEditingCard(null)}
              className="flashcard-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                await updateCard(editingCard.id, editingCard);
                setEditingCard(null);
              }}
              className="flashcard-save-btn"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderManualAddModal = () => {
    if (!showManualAddModal) return null;

    const quickEmojis = ['😊', '🔥', '💡', '⚡', '🎯', '💪', '🌟', '🚀'];

    return (
      <div className="flashcard-edit-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={() => {
            if (!isAddingManualCard) closeManualAddModal();
          }}
        />
        <div className="flashcard-modal-content flashcard-edit-modal-content flashcard-manual-modal-content">
          <div className="flashcard-edit-header">
            <div className="flashcard-edit-header-left">
              <div className="flashcard-edit-word-display">
                <FiPlus />
                <h2>Add a flashcard manually</h2>
              </div>
              <p className="flashcard-edit-definition">
                Missing details are auto-completed if the word already exists in
                Liwaza&apos;s GRE library.
              </p>
            </div>
            <button
              onClick={closeManualAddModal}
              className="flashcard-close-modal-btn"
              disabled={isAddingManualCard}
            >
              ×
            </button>
          </div>

          <div className="flashcard-modal-body">
            {manualAddError && (
              <div className="flashcard-manual-feedback flashcard-manual-feedback-error">
                {manualAddError}
              </div>
            )}

            <div className="flashcard-edit-section">
              <label className="flashcard-edit-section-label">Word</label>
              <input
                type="text"
                value={manualCardDraft.word}
                onChange={(e) =>
                  handleManualDraftChange('word', e.target.value)
                }
                placeholder="Enter a new word"
                className="flashcard-edit-input"
                autoFocus
              />
            </div>

            <div className="flashcard-manual-grid">
              <div className="flashcard-edit-section">
                <label className="flashcard-edit-section-label">
                  Definition (EN)
                </label>
                <textarea
                  value={manualCardDraft.definition_en}
                  onChange={(e) =>
                    handleManualDraftChange('definition_en', e.target.value)
                  }
                  placeholder="Optional if the word exists in the GRE library"
                  rows={3}
                  className="flashcard-edit-textarea"
                />
              </div>

              <div className="flashcard-edit-section">
                <label className="flashcard-edit-section-label">
                  Definition (FR)
                </label>
                <textarea
                  value={manualCardDraft.definition_fr}
                  onChange={(e) =>
                    handleManualDraftChange('definition_fr', e.target.value)
                  }
                  placeholder="Optional French meaning"
                  rows={3}
                  className="flashcard-edit-textarea"
                />
              </div>
            </div>

            <div className="flashcard-manual-grid">
              <div className="flashcard-edit-section">
                <label className="flashcard-edit-section-label">
                  Example (EN)
                </label>
                <textarea
                  value={manualCardDraft.example_sentence_en}
                  onChange={(e) =>
                    handleManualDraftChange(
                      'example_sentence_en',
                      e.target.value
                    )
                  }
                  placeholder="Optional example sentence in English"
                  rows={3}
                  className="flashcard-edit-textarea"
                />
              </div>

              <div className="flashcard-edit-section">
                <label className="flashcard-edit-section-label">
                  Example (FR)
                </label>
                <textarea
                  value={manualCardDraft.example_sentence_fr}
                  onChange={(e) =>
                    handleManualDraftChange(
                      'example_sentence_fr',
                      e.target.value
                    )
                  }
                  placeholder="Optional example sentence in French"
                  rows={3}
                  className="flashcard-edit-textarea"
                />
              </div>
            </div>

            <div className="flashcard-edit-section">
              <label className="flashcard-edit-section-label">Difficulty</label>
              <div className="flashcard-difficulty-chips">
                {[
                  { value: 'easy', label: 'Easy', color: 'green' },
                  { value: 'medium', label: 'Medium', color: 'amber' },
                  { value: 'hard', label: 'Hard', color: 'red' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`flashcard-difficulty-chip flashcard-difficulty-chip--${opt.color} ${
                      manualCardDraft.difficulty_level === opt.value
                        ? 'active'
                        : ''
                    }`}
                    onClick={() =>
                      handleManualDraftChange('difficulty_level', opt.value)
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flashcard-manual-grid flashcard-manual-grid-compact">
              <div className="flashcard-edit-section">
                <label className="flashcard-edit-section-label">Emoji</label>
                <div className="flashcard-emoji-picker-row">
                  <input
                    type="text"
                    value={manualCardDraft.emoticon}
                    onChange={(e) =>
                      handleManualDraftChange('emoticon', e.target.value)
                    }
                    placeholder="Optional"
                    maxLength={2}
                    className="flashcard-emoji-input-compact"
                  />
                  <div className="flashcard-emoji-quick-picks">
                    {quickEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className={`flashcard-emoji-pick-btn ${
                          manualCardDraft.emoticon === emoji ? 'active' : ''
                        }`}
                        onClick={() =>
                          handleManualDraftChange('emoticon', emoji)
                        }
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flashcard-edit-section">
                <label className="flashcard-edit-section-label">
                  Personal Note
                </label>
                <textarea
                  value={manualCardDraft.personalNote}
                  onChange={(e) =>
                    handleManualDraftChange('personalNote', e.target.value)
                  }
                  placeholder="Mnemonic, reminder, or translation cue"
                  rows={3}
                  className="flashcard-edit-textarea"
                />
              </div>
            </div>
          </div>

          <div className="flashcard-edit-actions">
            <button
              onClick={closeManualAddModal}
              className="flashcard-cancel-btn"
              disabled={isAddingManualCard}
            >
              Cancel
            </button>
            <button
              onClick={addManualFlashcard}
              className="flashcard-save-btn"
              disabled={isAddingManualCard}
            >
              {isAddingManualCard ? 'Saving...' : 'Add to my flashcards'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAdvancedAnalytics = () => {
    if (!showAdvancedAnalytics) return null;

    const a = getAnalyticsData();
    const maxReviewed = Math.max(...a.last30.map((d) => d.cardsReviewed), 1);

    const getRecommendations = () => {
      const recs = [];
      if (a.dueCards > 0) {
        recs.push({
          type: 'action',
          text: `You have ${a.dueCards} card${a.dueCards > 1 ? 's' : ''} due -- review now for best retention.`,
        });
      }
      if (a.daysSinceLastReview !== null && a.daysSinceLastReview >= 3) {
        recs.push({
          type: 'warning',
          text: `You haven't reviewed in ${a.daysSinceLastReview} day${a.daysSinceLastReview > 1 ? 's' : ''}. Your retention is dropping.`,
        });
      }
      if (a.retentionHard < a.retentionEasy && a.retentionHard < 60) {
        recs.push({
          type: 'tip',
          text: `Focus on Hard words -- your retention is ${a.retentionHard}% vs ${a.retentionEasy}% for Easy.`,
        });
      }
      if (a.totalCards < 50) {
        recs.push({
          type: 'tip',
          text: 'Add more words to your deck. Browse the GRE Library to reach at least 100 words.',
        });
      }
      if (recs.length === 0) {
        recs.push({
          type: 'success',
          text: 'Great job! Keep reviewing consistently to maintain your progress.',
        });
      }
      return recs;
    };

    const recommendations = getRecommendations();

    return (
      <div className="flashcard-edit-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={() => setShowAdvancedAnalytics(false)}
        />
        <div className="flashcard-modal-content flashcard-analytics-panel-content">
          <div className="flashcard-edit-header">
            <div className="flashcard-edit-header-left">
              <div className="flashcard-edit-word-display">
                <FiTrendingUp />
                <h2>Study Analytics</h2>
              </div>
            </div>
            <button
              onClick={() => setShowAdvancedAnalytics(false)}
              className="flashcard-close-modal-btn"
            >
              ×
            </button>
          </div>

          <div className="flashcard-modal-body flashcard-analytics-body">
            {/* GRE Vocab Readiness Score */}
            <div className="flashcard-analytics-section">
              <h3 className="flashcard-analytics-section-title">
                <FiStar /> GRE Vocab Readiness
              </h3>
              <div className="flashcard-readiness-bar-container">
                <div className="flashcard-readiness-bar">
                  <div
                    className="flashcard-readiness-fill"
                    style={{ width: `${a.readinessScore}%` }}
                  />
                </div>
                <span className="flashcard-readiness-score">
                  {a.readinessScore}%
                </span>
              </div>
              <p className="flashcard-analytics-detail">
                {a.masteredCards} / {a.TARGET_WORDS} words mastered
                {a.daysToTarget && a.daysToTarget > 0 && (
                  <span>
                    {' '}
                    — at your pace, ~{a.daysToTarget} days to target
                  </span>
                )}
              </p>
            </div>

            {/* Retention by difficulty */}
            <div className="flashcard-analytics-section">
              <h3 className="flashcard-analytics-section-title">
                <FiCheckCircle /> Retention Rate
              </h3>
              <div className="flashcard-retention-grid">
                <div className="flashcard-retention-item">
                  <span className="flashcard-retention-label">Overall</span>
                  <span className="flashcard-retention-value">
                    {a.overallRetention}%
                  </span>
                </div>
                <div className="flashcard-retention-item flashcard-retention-easy">
                  <span className="flashcard-retention-label">Easy</span>
                  <span className="flashcard-retention-value">
                    {a.retentionEasy}%
                  </span>
                </div>
                <div className="flashcard-retention-item flashcard-retention-medium">
                  <span className="flashcard-retention-label">Medium</span>
                  <span className="flashcard-retention-value">
                    {a.retentionMedium}%
                  </span>
                </div>
                <div className="flashcard-retention-item flashcard-retention-hard">
                  <span className="flashcard-retention-label">Hard</span>
                  <span className="flashcard-retention-value">
                    {a.retentionHard}%
                  </span>
                </div>
              </div>
            </div>

            {/* 30-day activity chart */}
            <div className="flashcard-analytics-section">
              <h3 className="flashcard-analytics-section-title">
                <FiCalendar /> 30-Day Review Activity
              </h3>
              <div className="flashcard-activity-chart">
                {a.last30.map((day) => (
                  <div
                    key={day.date}
                    className="flashcard-activity-bar-wrapper"
                    title={`${day.label}: ${day.cardsReviewed} reviews (${day.correct} correct)`}
                  >
                    <div
                      className="flashcard-activity-bar"
                      style={{
                        height: `${(day.cardsReviewed / maxReviewed) * 100}%`,
                      }}
                    />
                  </div>
                ))}
              </div>
              <div className="flashcard-activity-legend">
                <span>Avg {a.avgReviewsPerDay}/day reviewed</span>
                <span>Avg {a.avgAddedPerDay}/day added</span>
              </div>
            </div>

            {/* Weak Words */}
            {a.weakWords.length > 0 && (
              <div className="flashcard-analytics-section">
                <h3 className="flashcard-analytics-section-title">
                  <FiXCircle /> Hardest Words (lowest ease)
                </h3>
                <div className="flashcard-weak-words-grid">
                  {a.weakWords.map((w) => (
                    <div
                      key={w.id}
                      className="flashcard-weak-word"
                      title={`Ease: ${w.easeFactor.toFixed(2)}, Reviews: ${w.reviewCount}`}
                      onClick={() => setShowInfo(w)}
                    >
                      <span className="flashcard-weak-word-text">
                        {w.word}
                      </span>
                      <span
                        className="flashcard-weak-word-ease"
                        style={{
                          background: `hsl(${Math.max(0, (w.easeFactor - 1.3) / 1.2) * 120}, 70%, 50%)`,
                        }}
                      >
                        {w.easeFactor.toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="flashcard-analytics-section">
              <h3 className="flashcard-analytics-section-title">
                <FiZap /> Recommendations
              </h3>
              <div className="flashcard-recommendations">
                {recommendations.map((rec, i) => (
                  <div
                    key={i}
                    className={`flashcard-recommendation flashcard-recommendation-${rec.type}`}
                  >
                    {rec.text}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBrowseLibrary = () => {
    if (!showBrowseLibrary) return null;

    const letters = Array.from(
      new Set(greWordsData.map((w) => w.word.charAt(0).toUpperCase()))
    ).sort();

    const selectableCount = browseFilteredWords.filter(
      (w) => !existingWordsSet.has(normalizeFlashcardWord(w.word))
    ).length;

    return (
      <div className="flashcard-edit-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={() => !isAddingBrowse && setShowBrowseLibrary(false)}
        />
        <div className="flashcard-modal-content flashcard-browse-modal-content">
          <div className="flashcard-edit-header">
            <div className="flashcard-edit-header-left">
              <div className="flashcard-edit-word-display">
                <FiBook />
                <h2>Browse GRE Library</h2>
              </div>
              <p className="flashcard-edit-definition">
                {greWordsData.length} words available
                {browseSelected.size > 0 &&
                  ` — ${browseSelected.size} selected`}
              </p>
            </div>
            <button
              onClick={() => setShowBrowseLibrary(false)}
              className="flashcard-close-modal-btn"
              disabled={isAddingBrowse}
            >
              ×
            </button>
          </div>

          <div className="flashcard-browse-toolbar">
            <div className="flashcard-search-bar flashcard-browse-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Search words or definitions..."
                value={browseSearch}
                onChange={(e) => setBrowseSearch(e.target.value)}
              />
            </div>
            <select
              value={browseDifficulty}
              onChange={(e) => setBrowseDifficulty(e.target.value)}
              className="flashcard-browse-select"
            >
              <option value="all">All levels</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
            <select
              value={browseLetter}
              onChange={(e) => setBrowseLetter(e.target.value)}
              className="flashcard-browse-select"
            >
              <option value="all">All letters</option>
              {letters.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>

          <div className="flashcard-browse-info">
            Showing {browseFilteredWords.length} words ({selectableCount} can be
            added)
            {browseSelected.size > 0 && (
              <button
                className="flashcard-browse-select-clear"
                onClick={() => setBrowseSelected(new Set())}
              >
                Clear selection
              </button>
            )}
          </div>

          <div className="flashcard-browse-list">
            {browseFilteredWords.map((w) => {
              const nw = normalizeFlashcardWord(w.word);
              const inDeck = existingWordsSet.has(nw);
              const isSelected = browseSelected.has(w.word);

              return (
                <div
                  key={w.word}
                  className={`flashcard-browse-item ${inDeck ? 'flashcard-browse-in-deck' : ''} ${isSelected ? 'flashcard-browse-selected' : ''}`}
                  onClick={() => !inDeck && toggleBrowseSelect(w.word)}
                >
                  <div className="flashcard-browse-word-row">
                    {!inDeck && (
                      <span
                        className={`flashcard-browse-checkbox ${isSelected ? 'checked' : ''}`}
                      >
                        {isSelected ? '✓' : ''}
                      </span>
                    )}
                    <span className="flashcard-browse-word">{w.word}</span>
                    <span
                      className={`flashcard-difficulty-badge flashcard-difficulty-${(w.difficulty || 'medium').toLowerCase()}`}
                    >
                      {w.difficulty || 'Medium'}
                    </span>
                    {inDeck && (
                      <span className="flashcard-browse-in-deck-badge">
                        In deck
                      </span>
                    )}
                  </div>
                  <p className="flashcard-browse-def">{w.definition_en}</p>
                </div>
              );
            })}
          </div>

          <div className="flashcard-edit-actions">
            <button
              onClick={() => setShowBrowseLibrary(false)}
              className="flashcard-cancel-btn"
              disabled={isAddingBrowse}
            >
              Cancel
            </button>
            <button
              onClick={addBrowseSelectedWords}
              className="flashcard-save-btn"
              disabled={isAddingBrowse || browseSelected.size === 0}
            >
              {isAddingBrowse
                ? 'Adding...'
                : `Add ${browseSelected.size} word${browseSelected.size !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCsvImportPreview = () => {
    if (!csvImportPreview) return null;

    const previewCards = csvImportPreview.cards.slice(0, 50);
    const hiddenCount = csvImportPreview.cards.length - previewCards.length;
    const skippedCount =
      csvImportPreview.skippedDuplicates + csvImportPreview.skippedEmptyRows;

    return (
      <div className="flashcard-edit-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={closeCsvImportPreview}
        />
        <div className="flashcard-modal-content flashcard-csv-preview-modal-content">
          <div className="flashcard-edit-header">
            <div className="flashcard-edit-header-left">
              <div className="flashcard-edit-word-display">
                <FiUpload />
                <h2>Preview CSV import</h2>
              </div>
              <p className="flashcard-edit-definition">
                {csvImportPreview.fileName} - {csvImportPreview.cards.length} flashcard
                {csvImportPreview.cards.length !== 1 ? 's' : ''} ready to import
                {skippedCount > 0 ? `, ${skippedCount} skipped` : ''}.
              </p>
            </div>
            <button
              onClick={closeCsvImportPreview}
              className="flashcard-close-modal-btn"
              disabled={isImportingCsv}
            >
              ×
            </button>
          </div>

          <div className="flashcard-modal-body">
            <div className="flashcard-csv-preview-summary">
              <div className="flashcard-csv-preview-stat">
                <strong>{csvImportPreview.totalRows}</strong>
                <span>rows read</span>
              </div>
              <div className="flashcard-csv-preview-stat">
                <strong>{csvImportPreview.cards.length}</strong>
                <span>cards to import</span>
              </div>
              <div className="flashcard-csv-preview-stat">
                <strong>{csvImportPreview.skippedDuplicates}</strong>
                <span>duplicates skipped</span>
              </div>
              <div className="flashcard-csv-preview-stat">
                <strong>{csvImportPreview.skippedEmptyRows}</strong>
                <span>empty rows skipped</span>
              </div>
            </div>

            <div className="flashcard-csv-preview-list">
              {previewCards.map((card, index) => (
                <div key={`${card.word}-${index}`} className="flashcard-csv-preview-item">
                  <div className="flashcard-csv-preview-item-header">
                    <div>
                      <h3>{card.word}</h3>
                      <span className="flashcard-difficulty-badge">
                        {card.difficulty_level || 'medium'}
                      </span>
                    </div>
                  </div>
                  {card.definition_en && (
                    <p>
                      <strong>Definition (EN):</strong> {card.definition_en}
                    </p>
                  )}
                  {card.definition_fr && (
                    <p>
                      <strong>Definition (FR):</strong> {card.definition_fr}
                    </p>
                  )}
                  {card.example_sentence_en && (
                    <p>
                      <strong>Example (EN):</strong> {card.example_sentence_en}
                    </p>
                  )}
                  {card.example_sentence_fr && (
                    <p>
                      <strong>Example (FR):</strong> {card.example_sentence_fr}
                    </p>
                  )}
                  {card.personalNote && (
                    <p>
                      <strong>Note:</strong> {card.personalNote}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {hiddenCount > 0 && (
              <div className="flashcard-csv-preview-more">
                {hiddenCount} more flashcard{hiddenCount !== 1 ? 's' : ''} will also
                be imported.
              </div>
            )}
          </div>

          <div className="flashcard-edit-actions">
            <button
              onClick={closeCsvImportPreview}
              className="flashcard-cancel-btn"
              disabled={isImportingCsv}
            >
              Cancel
            </button>
            <button
              onClick={confirmCsvImport}
              className="flashcard-save-btn"
              disabled={isImportingCsv || csvImportPreview.cards.length === 0}
            >
              {isImportingCsv
                ? 'Importing...'
                : `Import ${csvImportPreview.cards.length} flashcard${
                    csvImportPreview.cards.length !== 1 ? 's' : ''
                  }`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewModeSelector = () => {
    if (!showReviewModeSelector) return null;

    const dueCount = getDueCardsCount();

    return (
      <div className="flashcard-edit-modal">
        <div
          className="flashcard-modal-overlay"
          onClick={() => setShowReviewModeSelector(false)}
        />
        <div className="flashcard-modal-content flashcard-edit-modal-content flashcard-mode-selector-content">
          <div className="flashcard-edit-header">
            <div className="flashcard-edit-header-left">
              <div className="flashcard-edit-word-display">
                <FiRotateCw />
                <h2>Choose Review Mode</h2>
              </div>
              <p className="flashcard-edit-definition">
                {dueCount > 0
                  ? `You have ${dueCount} card${dueCount > 1 ? 's' : ''} due for review.`
                  : 'Pick a mode to study your flashcards.'}
              </p>
            </div>
            <button
              onClick={() => setShowReviewModeSelector(false)}
              className="flashcard-close-modal-btn"
            >
              ×
            </button>
          </div>

          <div className="flashcard-modal-body">
            <div className="flashcard-mode-options">
              <button
                className="flashcard-mode-option"
                onClick={startQuizMode}
              >
                <div className="flashcard-mode-icon"><FiZap size={28} /></div>
                <div className="flashcard-mode-text">
                  <h3>Quiz Mode</h3>
                  <p>Multiple-choice questions with timer. Fast-paced and engaging.</p>
                </div>
              </button>

              <button
                className="flashcard-mode-option"
                onClick={startFlashcardMode}
              >
                <div className="flashcard-mode-icon"><FiBook size={28} /></div>
                <div className="flashcard-mode-text">
                  <h3>Flashcard Mode</h3>
                  <p>Classic flip cards. Rate your recall: Again, Hard, Good, or Easy.</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReviewMode = () => {
    if (reviewCards.length === 0) return null;

    const card = overlayCardWithGreWord(reviewCards[currentReviewIndex]);

    return (
      <div className="flashcard-review-mode">
        <div className="flashcard-review-header">
          <button
            onClick={() => setIsReviewMode(false)}
            className="flashcard-back-btn"
          >
            <FiArrowLeft /> Exit Review
          </button>
          <div className="flashcard-review-progress">
            Card {currentReviewIndex + 1} of {reviewCards.length}
          </div>
        </div>

        <div className="flashcard-review-card">
          <div className="flashcard-review-card-front">
            {card.emoticon && (
              <div className="flashcard-review-emoticon">{card.emoticon}</div>
            )}
            <h1 className="flashcard-review-word">{card.word}</h1>
            <button
              onClick={() => speakWord(card.word)}
              className="flashcard-pronunciation-btn-large"
            >
              <FiVolume2 size={24} /> Pronounce
            </button>
          </div>

          {!showAnswer ? (
            <button
              onClick={() => setShowAnswer(true)}
              className="flashcard-show-answer-btn"
            >
              Show Answer
            </button>
          ) : (
            <>
              <div className="flashcard-review-card-back">
                <div className="flashcard-review-definition">
                  <h3>Definition</h3>
                  <p>{card.definition_en}</p>
                </div>

                <NuanceCallout word={card.word} />

                {card.example_sentence_en && (
                  <div className="flashcard-review-example">
                    <h3>Example</h3>
                    <p className="flashcard-example-text">
                      "{card.example_sentence_en}"
                    </p>
                  </div>
                )}

                {card.personalNote && (
                  <div className="flashcard-review-note">
                    <h3>Your Note</h3>
                    <p>{card.personalNote}</p>
                  </div>
                )}
              </div>

              <div className="flashcard-rating-buttons">
                <button
                  onClick={() => rateCard(1)}
                  className="flashcard-rating-btn flashcard-rating-again"
                >
                  <FiXCircle />
                  Again
                  <span className="flashcard-rating-time">1 day</span>
                </button>
                <button
                  onClick={() => rateCard(3)}
                  className="flashcard-rating-btn flashcard-rating-hard"
                >
                  <FiClock />
                  Hard
                  <span className="flashcard-rating-time">
                    {Math.max(1, Math.round(card.interval * 0.5))} days
                  </span>
                </button>
                <button
                  onClick={() => rateCard(4)}
                  className="flashcard-rating-btn flashcard-rating-good"
                >
                  <FiCheckCircle />
                  Good
                  <span className="flashcard-rating-time">
                    {Math.round(card.interval * card.easeFactor)} days
                  </span>
                </button>
                <button
                  onClick={() => rateCard(5)}
                  className="flashcard-rating-btn flashcard-rating-easy"
                >
                  <FiZap />
                  Easy
                  <span className="flashcard-rating-time">
                    {Math.round(card.interval * card.easeFactor * 1.3)} days
                  </span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCardGrid = () => (
    <div className={`flashcard-grid ${viewMode}`}>
      {filteredCards.map((rawCard) => {
        const card = overlayCardWithGreWord(rawCard);
        const status = getCardStatus(card);
        const isDue = new Date(card.nextReviewDate) <= new Date();
        const nuance = getNuanceForWord(card.word);

        return (
          <div
            key={card.id}
            className={`flashcard-item flashcard-item-${status} ${isDue ? 'flashcard-item-due' : ''} ${nuance?.isFalseFriend ? 'flashcard-item-faux-ami' : ''}`}
          >
            <div className="flashcard-card-header">
              {card.emoticon && (
                <span className="flashcard-card-emoticon">{card.emoticon}</span>
              )}
              <h3 className="flashcard-card-word">{card.word}</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  speakWord(card.word);
                }}
                className="flashcard-mini-pronunciation-btn"
              >
                <FiVolume2 size={14} />
              </button>
            </div>

            <p className="flashcard-card-definition">{card.definition_en}</p>

            <div className="flashcard-card-footer">
              <span
                className={`flashcard-difficulty-badge flashcard-difficulty-${card.difficulty_level}`}
              >
                {card.difficulty_level}
              </span>
              <span
                className={`flashcard-status-badge flashcard-status-${status}`}
              >
                {status}
              </span>
              {isDue && <span className="flashcard-due-badge">Due</span>}
              {nuance?.isFalseFriend && (
                <span className="flashcard-faux-ami-badge">⚠️ Faux ami</span>
              )}
            </div>

            <div className="flashcard-card-actions">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInfo(card);
                }}
                className="flashcard-action-btn flashcard-action-info"
                title="Know more"
              >
                <FiBook size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAnalytics(card);
                }}
                className="flashcard-action-btn flashcard-action-analytics"
                title="View analytics"
              >
                <FiTrendingUp size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingCard(card);
                }}
                className={`flashcard-action-btn ${!card.emoticon ? 'flashcard-action-edit-highlight' : ''}`}
                title={!card.emoticon ? 'Add emoticon & notes' : 'Edit'}
              >
                <FiEdit2 size={14} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteCard(card.id);
                }}
                className="flashcard-action-btn flashcard-action-delete"
                title="Delete"
              >
                <FiTrash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (isReviewMode) {
    return (
      <div className="layout">
        <Sidebar />
        <main className="content">
          <div className="flashcard-book">{renderReviewMode()}</div>
        </main>
      </div>
    );
  }

  return (
    <div className="layout">
      <Sidebar />
      <main className="content">
        <div className="flashcard-book">
          {/* Header */}
          <div className="flashcard-header">
            <button
              onClick={() => navigate(-1)}
              className="flashcard-back-button"
            >
              <FiArrowLeft /> Back
            </button>
            <h1>
              <FiBookmark /> My Flashcard Book
            </h1>
          </div>

          {/* Stats */}
          {renderStats()}

          {/* How to use guide */}
          <div className="flashcard-guide">
            <button
              className="flashcard-guide-toggle"
              onClick={() => {
                const next = !showGuide;
                setShowGuide(next);
                if (!next) {
                  try { localStorage.setItem(`flashcard_guide_dismissed_${userEmail}`, 'true'); } catch {}
                }
              }}
            >
              <FiInfo size={16} />
              <span>How Flashcards Work</span>
              {showGuide ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </button>
            {showGuide && (
              <div className="flashcard-guide-body">
                <div className="flashcard-guide-steps">
                  <div className="flashcard-guide-step">
                    <span className="flashcard-guide-step-num">1</span>
                    <div>
                      <strong>Build your deck</strong>
                      <p>Add words manually, import a CSV, browse the GRE library, or click "Add to Flashcards" in any vocabulary article.</p>
                    </div>
                  </div>
                  <div className="flashcard-guide-step">
                    <span className="flashcard-guide-step-num">2</span>
                    <div>
                      <strong>Review regularly</strong>
                      <p>Click "Play Words" to quiz yourself or use flip-card mode. The spaced repetition system schedules words at the ideal time.</p>
                    </div>
                  </div>
                  <div className="flashcard-guide-step">
                    <span className="flashcard-guide-step-num">3</span>
                    <div>
                      <strong>Track progress</strong>
                      <p>Words move from New → Learning → Mastered. Check Analytics for your readiness score and retention rate.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flashcard-action-bar">
            <button
              onClick={startReview}
              className="flashcard-review-btn flashcard-review-btn-primary"
              disabled={flashcards.length === 0}
            >
              <FiRotateCw /> Play Words ({getPlayableWordsCount()})
            </button>

            <button
              onClick={openManualAddModal}
              className="flashcard-review-btn flashcard-review-btn-secondary"
            >
              <FiPlus /> Add Manually
            </button>

            <button
              onClick={openBrowseLibrary}
              className="flashcard-review-btn flashcard-review-btn-secondary"
            >
              <FiBook /> Browse GRE Library
            </button>

            <button
              onClick={openCsvImportPicker}
              className="flashcard-review-btn flashcard-review-btn-secondary"
              disabled={isImportingCsv}
            >
              <FiUpload /> {isImportingCsv ? 'Importing CSV...' : 'Import CSV'}
            </button>

            <div className="flashcard-import-help">
              <button
                type="button"
                className="flashcard-import-help-trigger"
                aria-label="CSV import format help"
                onClick={() => setShowCsvFormatInfo((current) => !current)}
              >
                <FiInfo />
              </button>
              {showCsvFormatInfo && (
                <div className="flashcard-import-help-bubble">
                  <strong>CSV format</strong>
                  <p>
                    Include a header row with at least `Word` or `Mot`.
                    Optional columns: `Definition (EN)`, `Definition (FR)`,
                    `Example (EN)`, `Example (FR)`, `Difficulty`, `Note`.
                  </p>
                  <p>
                    Comma, semicolon, and tab-separated files are supported.
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={downloadFlashcardsPdf}
              className="flashcard-review-btn flashcard-review-btn-secondary"
              disabled={flashcards.length === 0 || isExportingPdf}
            >
              <FiDownload /> {isExportingPdf ? 'Preparing PDF...' : 'Download PDF'}
            </button>

            <button
              onClick={() => setShowAdvancedAnalytics(true)}
              className="flashcard-review-btn flashcard-review-btn-secondary"
              disabled={flashcards.length === 0}
            >
              <FiTrendingUp /> Analytics
            </button>

            <div className="flashcard-search-bar">
              <FiSearch />
              <input
                type="text"
                placeholder="Search flashcards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flashcard-filter-toggle ${showFilters ? 'flashcard-filter-active' : ''}`}
            >
              <FiFilter /> Filters
            </button>
          </div>

          {/* Filters */}
          {renderFilters()}

          {/* Results Count */}
          <div className="flashcard-results-info">
            Showing {filteredCards.length} of {flashcards.length} words
          </div>

          <input
            ref={csvImportInputRef}
            type="file"
            accept=".csv,.tsv,text/csv,text/tab-separated-values"
            onChange={handleCsvFileSelected}
            style={{ display: 'none' }}
          />

          {actionFeedback.message && (
            <div
              className={`flashcard-manual-feedback ${
                actionFeedback.type === 'error'
                  ? 'flashcard-manual-feedback-error'
                  : 'flashcard-manual-feedback-success'
              }`}
            >
              {actionFeedback.message}
            </div>
          )}

          {/* Card Grid */}
          {filteredCards.length > 0 ? (
            renderCardGrid()
          ) : (
            <div className="flashcard-empty-state">
              <FiBookmark size={64} />
              <h2>No flashcards found</h2>
              <p>
                {flashcards.length === 0
                  ? 'Start adding words from your daily articles, import a CSV, or create one manually to build your flashcard collection!'
                  : 'Try adjusting your filters or search query.'}
              </p>
              {flashcards.length === 0 && (
                <div className="flashcard-empty-actions">
                  <button
                    onClick={openManualAddModal}
                    className="flashcard-empty-action"
                  >
                    <FiPlus /> Create my first flashcard
                  </button>
                  <button
                    onClick={openCsvImportPicker}
                    className="flashcard-empty-action flashcard-empty-action-secondary"
                    disabled={isImportingCsv}
                  >
                    <FiUpload /> {isImportingCsv ? 'Importing CSV...' : 'Import a CSV'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Modals */}
          {showInfo && renderCardDetails(showInfo)}
          {showAnalytics && renderAnalyticsModal()}
          {editingCard && renderEditModal()}
          {showManualAddModal && renderManualAddModal()}
          {showReviewModeSelector && renderReviewModeSelector()}
          {showBrowseLibrary && renderBrowseLibrary()}
          {csvImportPreview && renderCsvImportPreview()}
          {showAdvancedAnalytics && renderAdvancedAnalytics()}
        </div>
      </main>

      {/* GRE Words Challenge Modal */}
      {showWordsChallenge && (
        <GREWordsChallenge
          availableWords={getAllCardsForOptions()}
          customWords={getDueCardsForChallenge()}
          onClose={() => setShowWordsChallenge(false)}
          onSessionStarted={() => {
            markHomeTaskEvent(userEmail, 'gre-vocab-challenge', 'started');
            markHomeTaskEvent(userEmail, 'weekend-vocab', 'started');
          }}
          onSessionFinished={() => {
            markHomeTaskEvent(userEmail, 'gre-vocab-challenge', 'finished');
            markHomeTaskEvent(userEmail, 'weekend-vocab', 'finished');
          }}
          onCardReviewed={handleCardReviewed}
          initialGameMode="riddle"
          initialWordCount={Math.max(1, Math.min(30, getPlayableWordsCount()))}
          initialTimeBetweenQuestions={10}
          autoStart={false}
          showPremiumLimits={false}
          hideRiddleMode={false}
          showTextProgress={true}
          hideWordCountSelector={false}
          wordPoolOptions={[
            { key: 'thisWeek', label: 'Words of the week', description: 'Added in the last 7 days' },
            { key: 'due', label: 'Due', description: 'Due for review now' },
            { key: 'lastAdded', label: 'Last added', description: 'Play the most recent words you saved' },
            { key: 'new', label: 'New', description: 'Never reviewed yet' },
            { key: 'learning', label: 'Learning', description: 'In progress' },
            { key: 'mastered', label: 'Mastered', description: 'Strong words' },
            { key: 'filtered', label: 'Current filters', description: 'Use current search + filters' },
            { key: 'all', label: 'All words', description: 'Your full deck' },
          ]}
          initialWordPoolKey="thisWeek"
          externalWordPools={{
            filtered: getFilteredCardsForChallenge(),
            due: getDueCardsForChallenge(),
          }}
        />
      )}
    </div>
  );
};

export default FlashcardBook;
